import { useEffect } from "react";
import { create } from "zustand";
import type { LucideIcon } from "lucide-react";

/**
 * The grooming command palette's open state and command registry (epic
 * PI-SPEC-SW-E280, feature PI-SPEC-SW-1399).
 *
 * The palette builds its own "Go to" and item commands. Pages add commands
 * that only make sense while they are open (the backlog's "switch to
 * Board/List", Present mode's "Present the agenda") with
 * {@link useRegisterCommands}; they disappear when the page unmounts.
 */

export interface GroomingCommand {
  /** Unique within the palette. */
  id: string;
  label: string;
  /** Short text on the right, for example a shortcut or an item ID. */
  hint?: string;
  icon?: LucideIcon;
  /** Extra words the filter matches besides the label. */
  keywords?: string;
  /**
   * A section heading shown above the first command of a run with the same
   * section (frame D5: "Item from the link"; feature PI-SPEC-SW-1495).
   */
  section?: string;
  /** A second, smaller line under the label; the row grows to two lines. */
  description?: string;
  /** A part of the label shown bold (frame D5: the link's project). */
  emphasis?: string;
  /** Words on the right naming what the row does, instead of a hint (frame D5: "Open in My ADO Items"). */
  action?: string;
  /** Classes for the icon instead of the faint default (frame D5: the blue switch-project arrows). */
  iconClassName?: string;
  run: () => void;
}

interface CommandPaletteState {
  open: boolean;
  /** Registered commands by source (a page or component name). */
  sources: Record<string, readonly GroomingCommand[]>;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  register: (source: string, commands: readonly GroomingCommand[]) => void;
  unregister: (source: string) => void;
}

export const useCommandPaletteStore = create<CommandPaletteState>((set) => ({
  open: false,
  sources: {},
  setOpen: (open) => {
    set({ open });
  },
  toggle: () => {
    set((state) => ({ open: !state.open }));
  },
  register: (source, commands) => {
    set((state) => ({ sources: { ...state.sources, [source]: commands } }));
  },
  unregister: (source) => {
    set((state) => {
      const { [source]: _removed, ...rest } = state.sources;
      return { sources: rest };
    });
  },
}));

/** Open the palette (the title bar's search button). */
export function openCommandPalette(): void {
  useCommandPaletteStore.getState().setOpen(true);
}

/** Every registered command, in registration order. */
export function registeredCommands(sources: Record<string, readonly GroomingCommand[]>): GroomingCommand[] {
  return Object.values(sources).flat();
}

/**
 * Register commands while the calling component is mounted. Pass a memoised
 * array: a new array re-registers on every render.
 */
export function useRegisterCommands(source: string, commands: readonly GroomingCommand[]): void {
  const register = useCommandPaletteStore((state) => state.register);
  const unregister = useCommandPaletteStore((state) => state.unregister);
  useEffect(() => {
    register(source, commands);
    return () => {
      unregister(source);
    };
  }, [source, commands, register, unregister]);
}

/** The platform's palette shortcut label: ⌘K on a Mac, Ctrl K elsewhere. */
export function paletteShortcutLabel(platform?: string): string {
  const name = platform ?? (typeof navigator === "undefined" ? "" : navigator.userAgent);
  return /mac|darwin|iphone|ipad/i.test(name) ? "⌘K" : "Ctrl K";
}
